import {
  CATEGORIES_VERS_METIER,
  MOTS_CLES_SERVICES,
  descriptionService,
  estCategorieGenerique,
  metierDepuisCategories,
  metierDepuisTexteLibre,
  normaliserTexte
} from '../config/servicesCatalog';
import { parseOpeningHours } from './horaires';
import { scoreProspect } from './prospectScoring';

/**
 * Import Apify → `prospects` : parsing, normalisation, merge. **Tout est pur.**
 *
 * Aucun `Timestamp` Firestore n'entre ni ne sort d'ici (piège n°7) : les dates sont des `Date`,
 * la conversion est faite par `prospectService.importProspects()`. C'est ce qui rend les 16 tests
 * exécutables sans émulateur.
 *
 * Le fichier de référence (run du 29/10/2026, 50 fiches `plombier` / Oise) est un JSON `[{…}]`
 * de `compass/crawler-google-places` en `language: "fr"`, `website: "withoutWebsite"`.
 */

/** Longueur maximale d'une ligne d'aperçu, pour les rejets. */
export const APERCU_MAX = 120;

/** Motifs de rejet — jamais silencieux : chacun est compté et listé dans le récapitulatif. */
export const MOTIFS_REJET = {
  SANS_PLACEID: 'sans_placeid',
  SANS_NOM: 'sans_nom',
  FERME_DEFINITIVEMENT: 'ferme_definitivement',
  INVALIDE: 'invalide'
};

export const MOTIFS_REJET_LABELS = {
  [MOTIFS_REJET.SANS_PLACEID]: 'sans placeId (aucune clé de document possible)',
  [MOTIFS_REJET.SANS_NOM]: 'sans nom',
  [MOTIFS_REJET.FERME_DEFINITIVEMENT]: 'fermé définitivement',
  [MOTIFS_REJET.INVALIDE]: 'entrée illisible'
};

export const MOTIFS_EXCLUSION = {
  SANS_TELEPHONE: 'sans_telephone',
  HORS_CIBLE: 'hors_cible',
  FAUX_NUMERO: 'faux_numero',
  LIGNE_HS: 'ligne_hs',
  FERME: 'ferme'
};

/** La regex exacte de `validateStep2` du portail (`^(\\+33|0)\\d{9}$`). */
export const TELEPHONE_REGEX = /^(\+33|0)\d{9}$/;

/**
 * Numéro normalisé : espaces, points, tirets et parenthèses retirés
 * (`06 12 34 56 78` → `0612345678`). `null` s'il n'y a rien à normaliser.
 *
 * C'est **la même** normalisation que l'import et **la clé de dédoublonnage** du formulaire manuel.
 */
export const normaliserTelephone = (valeur) => {
  const brut = texteOuNull(valeur);
  return brut ? brut.replace(/[\s.\-()]/g, '') : null;
};

/**
 * Clé de comparaison d'un numéro : `+33612345678` et `0612345678` donnent la même clé.
 *
 * Sert **uniquement** au dédoublonnage — le stockage garde la forme d'origine
 * (`normaliserTelephone`), parce que le portail valide `^(\\+33|0)\\d{9}$` sur la valeur écrite.
 */
export const cleTelephone = (valeur) => {
  const normalise = normaliserTelephone(valeur);
  return normalise ? normalise.replace(/^\+33/, '0') : null;
};

/** Retire le BOM d'un fichier réenregistré par Excel (piège n°5). */
export const nettoyerContenu = (contenu) => String(contenu ?? '').replace(/^\uFEFF/, '');

/** `null` plutôt que `undefined` partout : Firestore refuse `undefined`. */
const texteOuNull = (valeur) => {
  if (valeur === null || valeur === undefined) return null;
  const texte = String(valeur).trim();
  return texte ? texte : null;
};

const nombreOuNull = (valeur) => {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  const nombre = Number(valeur);
  return Number.isFinite(nombre) ? nombre : null;
};

const booleenOuNull = (valeur) => {
  if (valeur === true || valeur === 'true' || valeur === 1 || valeur === '1') return true;
  if (valeur === false || valeur === 'false' || valeur === 0 || valeur === '0') return false;
  return null;
};

/** Date valide ou `null` — jamais `Invalid Date` en base. */
export const dateOuNull = (valeur) => {
  if (!valeur) return null;
  if (valeur instanceof Date) return Number.isNaN(valeur.getTime()) ? null : valeur;
  const date = new Date(valeur);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const apercuTexte = (valeur, max = APERCU_MAX) => {
  const texte = String(valeur ?? '').replace(/\s+/g, ' ').trim();
  return texte.length > max ? `${texte.slice(0, max - 1)}…` : texte;
};

/* ────────────────────────────── 1. Détection du format ────────────────────────────── */

/**
 * Détecte le format sur le contenu (BOM retiré), sans rien parser.
 * @returns {'json'|'jsonl'|'csv'|null}
 */
export const detectFormat = (contenu) => {
  const texte = nettoyerContenu(contenu).trim();
  if (!texte) return null;

  if (texte.startsWith('[')) return 'json';

  if (texte.startsWith('{')) {
    // Objet unique enveloppé (`{ "items": […] }`) → JSON ; un `{` par ligne → JSONL.
    const lignes = texte.split(/\r?\n/).filter((ligne) => ligne.trim().length > 0);
    const debutsAccolade = lignes.filter((ligne) => ligne.trim().startsWith('{')).length;
    if (debutsAccolade > 1 && lignes[0].trim().endsWith('}')) return 'jsonl';
    return 'json';
  }

  if (texte.includes(';') || texte.includes(',')) return 'csv';

  return null;
};

/** Vrai si l'export a été réenregistré en CP1252 (Excel Windows) : accents cassés. */
export const sembleCp1252 = (contenu) => String(contenu ?? '').includes('\uFFFD');

/** Table d'alias des colonnes : champ cible → en-têtes acceptés (normalisés). */
export const ALIAS_COLONNES = {
  placeId: ['placeid', 'id'],
  cid: ['cid'],
  name: ['title', 'nom', 'name'],
  phone: ['phoneunformatted', 'phone', 'telephone'],
  'address.street': ['street', 'rue', 'adresse'],
  'address.postalCode': ['postalcode', 'codepostal', 'cp'],
  'address.city': ['city', 'ville'],
  'address.formatted': ['adressecomplete'],
  categories: ['categories'],
  'google.claimed': ['claimthisbusiness', 'revendiquee'],
  'google.ownerDescription': ['ownerdescription', 'description'],
  'google.mapsUrl': ['url', 'mapsurl', 'lien'],
  'google.rating': ['totalscore', 'rating', 'note'],
  'google.reviewCount': ['reviewscount', 'reviews', 'avis'],
  'location.lat': ['lat', 'latitude'],
  'location.lng': ['lng', 'longitude'],
  'google.permanentlyClosed': ['permanentlyclosed', 'fermedefinitivement'],
  primaryCategory: ['categoryname']
};

/** `Code postal` → `codepostal` : casse, accents, espaces et soulignés sont ignorés. */
const normaliserEntete = (entete) => normaliserTexte(entete).replace(/[\s_-]/g, '');

const ALIAS_INVERSE = Object.entries(ALIAS_COLONNES).reduce((acc, [champ, alias]) => {
  alias.forEach((nom) => {
    if (!acc[nom]) acc[nom] = champ;
  });
  return acc;
}, {});

/** Affecte `valeur` au chemin pointé (`address.street`) d'un objet. */
const affecter = (cible, chemin, valeur) => {
  const [racine, enfant] = chemin.split('.');
  if (!enfant) {
    cible[racine] = valeur;
    return;
  }
  cible[racine] = cible[racine] && typeof cible[racine] === 'object' ? cible[racine] : {};
  cible[racine][enfant] = valeur;
};

/** Détecte le séparateur CSV sur la première ligne, hors guillemets (Excel FR = `;`). */
const detecterSeparateur = (ligne) => {
  let dansGuillemets = false;
  let pointsVirgules = 0;
  let virgules = 0;

  for (const caractere of String(ligne ?? '')) {
    if (caractere === '"') {
      dansGuillemets = !dansGuillemets;
    } else if (!dansGuillemets) {
      if (caractere === ';') pointsVirgules += 1;
      if (caractere === ',') virgules += 1;
    }
  }

  return pointsVirgules >= virgules ? ';' : ',';
};

/* ────────────────────────────── 2. Les parseurs ────────────────────────────── */

/** `{ "items": […] }` / `{ "data": […] }` sont des variantes d'API réelles. */
const extraireTableau = (valeur) => {
  if (Array.isArray(valeur)) return valeur;
  if (valeur && typeof valeur === 'object') {
    const cle = ['items', 'data', 'results', 'dataset'].find((nom) => Array.isArray(valeur[nom]));
    if (cle) return valeur[cle];
    return [valeur];
  }
  return [];
};

export const parseJson = (contenu) => extraireTableau(JSON.parse(nettoyerContenu(contenu)));

/** Une ligne = un objet ; une ligne illisible est comptée, jamais bloquante. */
export const parseJsonl = (contenu) => {
  const items = [];
  let erreurs = 0;

  nettoyerContenu(contenu)
    .split(/\r?\n/)
    .map((ligne) => ligne.trim())
    .filter(Boolean)
    .forEach((ligne) => {
      try {
        items.push(JSON.parse(ligne));
      } catch (error) {
        erreurs += 1;
      }
    });

  return { items, erreurs };
};

/**
 * Parseur CSV écrit à la main (~40 lignes, **sans `papaparse`**).
 * Analyse caractère par caractère : un champ peut contenir le séparateur entre guillemets,
 * `""` est un guillemet littéral, et un retour à la ligne entre guillemets est conservé
 * (les adresses multi-lignes existent).
 */
export const parseCsvLignes = (contenu) => {
  const texte = nettoyerContenu(contenu);
  const premiereLigne = texte.split(/\r?\n/)[0];
  const separateur = detecterSeparateur(premiereLigne);

  const lignes = [];
  let ligne = [];
  let champ = '';
  let dansGuillemets = false;

  for (let index = 0; index < texte.length; index += 1) {
    const caractere = texte[index];

    if (dansGuillemets) {
      if (caractere === '"') {
        if (texte[index + 1] === '"') {
          champ += '"';
          index += 1;
        } else {
          dansGuillemets = false;
        }
      } else {
        champ += caractere;
      }
    } else if (caractere === '"') {
      dansGuillemets = true;
    } else if (caractere === separateur) {
      ligne.push(champ);
      champ = '';
    } else if (caractere === '\n') {
      ligne.push(champ);
      lignes.push(ligne);
      ligne = [];
      champ = '';
    } else if (caractere !== '\r') {
      champ += caractere;
    }
  }

  if (champ !== '' || ligne.length > 0) {
    ligne.push(champ);
    lignes.push(ligne);
  }

  return {
    separateur,
    lignes: lignes.filter((uneLigne) => uneLigne.some((cellule) => String(cellule).trim() !== ''))
  };
};

/**
 * CSV → objets, en appliquant la table d'alias. Une colonne inconnue est ignorée
 * silencieusement (pas d'échec pour un export enrichi à la main), une ligne trop courte est
 * complétée par `null`.
 */
export const parseCsv = (contenu) => {
  const { lignes } = parseCsvLignes(contenu);
  if (lignes.length < 2) return { items: [], erreurs: lignes.length === 0 ? 0 : 1 };

  const entetes = lignes[0].map((entete) => normaliserEntete(entete));
  const correspondances = entetes.map((entete) => ALIAS_INVERSE[entete] || null);

  const items = lignes.slice(1).map((cellules) => {
    const objet = {};
    correspondances.forEach((champ, index) => {
      if (!champ) return;
      const valeur = cellules[index];
      affecter(objet, champ, valeur === undefined ? null : String(valeur).trim() || null);
    });
    return objet;
  });

  return { items, erreurs: 0 };
};

/**
 * Détecte le format et parse.
 * @returns {{format: 'json'|'jsonl'|'csv'|null, items: Array, erreurs: number}}
 */
export const parseContent = (contenu) => {
  const format = detectFormat(contenu);

  if (!format) return { format: null, items: [], erreurs: 0 };

  if (format === 'csv') {
    const { items, erreurs } = parseCsv(contenu);
    return { format, items, erreurs };
  }

  if (format === 'jsonl') {
    const { items, erreurs } = parseJsonl(contenu);
    return { format, items, erreurs };
  }

  return { format, items: parseJson(contenu), erreurs: 0 };
};

/** Vrai si au moins une catégorie identifie un métier (C8 : `categoryName` n'est qu'indicatif). */
export const aCategoriesExploitables = (categories = []) =>
  Array.isArray(categories) &&
  categories.some((categorie) => {
    const brut = String(categorie ?? '').trim();
    if (!brut || estCategorieGenerique(brut)) return false;
    const normalisee = normaliserTexte(brut);
    return Object.keys(CATEGORIES_VERS_METIER).some((cle) => normaliserTexte(cle) === normalisee);
  });

/* ──────────────── 3. Reconstruction d'adresse, résumé, suggestions de services ──────────────── */

/**
 * C1 — 13 fiches sur 50 n'ont **ni `street`, ni `city`, ni `postalCode`**, mais ont la chaîne
 * complète. On tente un découpage ; si aucun code postal à 5 chiffres n'est trouvé, les trois
 * champs restent `null`. **On ne devine jamais** : une adresse inventée est pire qu'une adresse
 * manquante, parce qu'elle n'est pas détectée.
 *
 * `"12 Rue des Lilas, 60000 Beauvais, France"` → street / postalCode / city
 */
export const reconstruireAdresse = (formatted) => {
  const texte = texteOuNull(formatted);
  if (!texte) return { street: null, postalCode: null, city: null };

  const match = /(\d{5})\s+([^,]+)/.exec(texte);
  if (!match) return { street: null, postalCode: null, city: null };

  const postalCode = match[1];
  const city = texteOuNull(match[2].replace(/\s+france$/i, '').trim());
  const street = texteOuNull(
    texte
      .slice(0, match.index)
      .replace(/[,\s]+$/, '')
      .trim()
  );

  return { street, postalCode, city };
};

const LIBELLES_METIERS = {
  plomberie: 'Plomberie',
  chauffage: 'Chauffage',
  electricite: 'Électricité',
  maconnerie: 'Maçonnerie',
  couverture: 'Couverture',
  carrelage: 'Carrelage',
  serrurerie: 'Serrurerie',
  ramonage: 'Ramonage',
  menuiserie: 'Menuiserie',
  isolation: 'Isolation',
  climatisation: 'Climatisation',
  peinture: 'Peinture'
};

/**
 * `activitySummary` doit tenir dans les bornes de `validateStep3` du portail : **50 à 500 car.**
 *  - 50 ≤ len ≤ 500 → copié tel quel
 *  - len > 500 → troncature à la dernière phrase complète avant 500, **jamais en plein mot**
 *  - len < 50 → complété pour franchir le seuil
 *  - absent (17 fiches) → `null`, le résumé sera rédigé pendant l'appel
 */
export const bornerActivitySummary = (description, { metier = null, ville = null } = {}) => {
  const brut = texteOuNull(description);
  if (!brut) return { activitySummary: null, summaryNeedsReview: false };

  // Texte suspect : lien, spam promotionnel, MAJUSCULES hurlées
  const summaryNeedsReview =
    /https?:\/\//i.test(brut) || /[A-Z]{12,}/.test(brut) || /promo|gratuit\s*!{2,}/i.test(brut);

  let texte = brut.replace(/\s+/g, ' ').trim();

  if (texte.length > 500) {
    const coupe = texte.slice(0, 500);
    const dernierPoint = Math.max(
      coupe.lastIndexOf('. '),
      coupe.lastIndexOf('! '),
      coupe.lastIndexOf('? ')
    );

    if (dernierPoint >= 50) {
      texte = coupe.slice(0, dernierPoint + 1).trim();
    } else {
      const dernierEspace = coupe.lastIndexOf(' ');
      texte = `${coupe.slice(0, dernierEspace > 0 ? dernierEspace : 500).trim()}…`;
    }
  }

  if (texte.length < 50) {
    const complements = [
      metier && ville ? `${LIBELLES_METIERS[metier] || metier} à ${ville}.` : null,
      ville ? `Interventions à ${ville} et alentours.` : null,
      'Interventions auprès des particuliers et des professionnels.'
    ].filter(Boolean);

    let index = 0;
    while (texte.length < 50 && index < complements.length) {
      texte = `${texte.replace(/\.$/, '')}. ${complements[index]}`.trim();
      index += 1;
    }
  }

  return { activitySummary: texte.length <= 500 ? texte : `${texte.slice(0, 499)}…`, summaryNeedsReview };
};

/** Le seul service ajouté par croisement de catégories (jamais tout le catalogue). */
const SERVICE_DU_METIER = {
  plomberie: 'Plomberie',
  chauffage: 'Chauffage',
  electricite: 'Électricité',
  maconnerie: 'Maçonnerie',
  couverture: 'Couverture',
  carrelage: 'Carrelage',
  serrurerie: 'Serrurerie',
  ramonage: 'Ramonage',
  menuiserie: 'Menuiserie',
  isolation: 'Isolation',
  climatisation: 'Climatisation',
  peinture: 'Peinture'
};

/**
 * Suggestions de services, **aide à l'appel et rien d'autre** : jamais injectées dans un
 * formulaire client. Règles : ne jamais inventer, croiser avec les catégories, plafonner à 8,
 * `price: 'Sur devis'` par défaut, `source` = ce qui a été deviné.
 *
 * @returns {Array<{name: string, description: string, price: string, source: string}>}
 */
export const extraireServices = (description, categories = [], ville = null, max = 8) => {
  const services = [];

  const ajouter = (nom, source) => {
    if (services.length >= max) return;
    const existe = services.some((service) => normaliserTexte(service.name) === normaliserTexte(nom));
    if (existe) return;
    services.push({
      name: nom,
      description: descriptionService(nom, ville),
      price: 'Sur devis',
      source
    });
  };

  const texte = normaliserTexte(description);
  if (texte) {
    MOTS_CLES_SERVICES.forEach(({ motsCles, nom }) => {
      if (motsCles.some((motCle) => texte.includes(normaliserTexte(motCle)))) {
        ajouter(nom, 'ownerDescription');
      }
    });
  }

  const metier = metierDepuisCategories(categories);
  if (metier && SERVICE_DU_METIER[metier]) {
    ajouter(SERVICE_DU_METIER[metier], 'categories');
  }

  return services.slice(0, max);
};

/* ────────────────────────────── 4. normalizeItem() ────────────────────────────── */

/** `{ "1": 0, "2": 0, … }` → `{ 1: 0, 2: 0, … }` : 5 clés toujours présentes, jamais `undefined`. */
const normaliserDistribution = (distribution) => {
  const source = distribution && typeof distribution === 'object' ? distribution : {};
  return [1, 2, 3, 4, 5].reduce((acc, note) => {
    acc[note] = nombreOuNull(source[note] ?? source[String(note)]) ?? 0;
    return acc;
  }, {});
};

/** `2026-10-29` — la cohorte du tunnel est indexée sur cette clé (`source.runDate`). */
const formaterDateImport = (date) => {
  const jour = String(date.getDate()).padStart(2, '0');
  const mois = String(date.getMonth() + 1).padStart(2, '0');
  return `${date.getFullYear()}-${mois}-${jour}`;
};

/**
 * Normalise une fiche brute au schéma §2. **Aucun `undefined`, aucun `null` ambigu.**
 *
 * Les dates sortent en `Date` (jamais en `Timestamp`) : la conversion Firestore est faite par le
 * service (piège n°7). `milestones` est créé **dès l'import** avec les 4 jalons aval à `null` :
 * c'est ce qui rend le tunnel calculable à J+0 sans qu'aucun code n'ait à le déduire.
 *
 * @param {object} raw entrée brute du fichier (JSON, JSONL ou ligne CSV déjà mappée)
 * @param {{uid?: string|null, importedAt?: Date, source?: object}} options
 * @returns {{document: object}|{rejet: string, raw: object}}
 */
export const normalizeItem = (raw, { uid = null, importedAt = new Date(), source = {} } = {}) => {
  if (!raw || typeof raw !== 'object') {
    return { rejet: MOTIFS_REJET.INVALIDE, raw };
  }

  const placeId = texteOuNull(raw.placeId ?? raw.place_id ?? raw.id);
  if (!placeId) return { rejet: MOTIFS_REJET.SANS_PLACEID, raw };

  const nom = texteOuNull(raw.title ?? raw.name ?? raw.nom);
  if (!nom) return { rejet: MOTIFS_REJET.SANS_NOM, raw };

  const googleBrut = raw.google && typeof raw.google === 'object' ? raw.google : {};
  const permanentlyClosed =
    booleenOuNull(raw.permanentlyClosed ?? googleBrut.permanentlyClosed) === true;
  if (permanentlyClosed) return { rejet: MOTIFS_REJET.FERME_DEFINITIVEMENT, raw };

  const categories = Array.isArray(raw.categories)
    ? raw.categories.map((categorie) => texteOuNull(categorie)).filter(Boolean)
    : String(raw.categories ?? '')
        .split(/[;,]/)
        .map((categorie) => categorie.trim())
        .filter(Boolean);

  // ── Contact ──
  const phone = normaliserTelephone(raw.phoneUnformatted ?? raw.phone ?? raw.telephone);
  const phoneValid = phone ? TELEPHONE_REGEX.test(phone) : false;

  // ── Localisation (C1) ──
  const addressBrute = raw.address && typeof raw.address === 'object' ? raw.address : {};
  const formatted =
    typeof raw.address === 'string'
      ? texteOuNull(raw.address)
      : texteOuNull(addressBrute.formatted ?? raw.adresseComplete);

  // Le CSV produit une structure déjà imbriquée (`address.street`), le JSON Apify des champs
  // racine (`street`) : les deux formes sont acceptées.
  let street = texteOuNull(raw.street ?? addressBrute.street);
  let postalCode = texteOuNull(raw.postalCode ?? raw.codePostal ?? raw.cp ?? addressBrute.postalCode);
  let city = texteOuNull(raw.city ?? raw.ville ?? addressBrute.city);

  if (!street && !postalCode && !city && formatted) {
    const devinee = reconstruireAdresse(formatted);
    street = devinee.street;
    postalCode = devinee.postalCode;
    city = devinee.city;
  }

  const isComplete = Boolean(street && street.length >= 5 && /^\d{5}$/.test(postalCode || '') && city);

  const codePostalComplet = /^\d{5}$/.test(postalCode || '')
    ? postalCode
    : (/(\d{5})/.exec(formatted || '') || [])[1] || null;
  const departmentCode = codePostalComplet ? codePostalComplet.slice(0, 2) : null;

  const targetTrade = metierDepuisCategories(categories);
  const claimedBrut = booleenOuNull(raw.claimThisBusiness ?? raw.revendiquee);
  const claimed = claimedBrut === null ? null : !claimedBrut;

  const ownerDescription = texteOuNull(
    raw.ownerDescription ?? googleBrut.ownerDescription ?? raw.description
  );
  const { activitySummary, summaryNeedsReview } = bornerActivitySummary(ownerDescription, {
    metier: targetTrade,
    ville: city
  });
  const services = extraireServices(ownerDescription, categories, city);

  // ── Statut initial : la seule fonction qui décide, dans cet ordre (§3.2 d) ──
  let status = 'a_appeler';
  let exclusionReason = null;

  if (!phoneValid) {
    status = 'exclu';
    exclusionReason = MOTIFS_EXCLUSION.SANS_TELEPHONE;
  } else if (!aCategoriesExploitables(categories) && targetTrade === null) {
    status = 'exclu';
    exclusionReason = MOTIFS_EXCLUSION.HORS_CIBLE;
  } else if (!isComplete) {
    status = 'a_completer';
  }

  const instant = importedAt instanceof Date ? importedAt : new Date(importedAt);

  const document = {
    placeId,
    cid: texteOuNull(raw.cid),
    name: nom,
    categories,
    primaryCategory: texteOuNull(raw.categoryName ?? raw.primaryCategory),
    targetTrade,

    phone,
    phoneValid,
    email: null,

    address: { street, postalCode, city, formatted, isComplete },
    departmentCode,
    location: {
      lat: nombreOuNull((raw.location || {}).lat ?? raw.lat),
      lng: nombreOuNull((raw.location || {}).lng ?? raw.lng)
    },

    google: {
      mapsUrl: texteOuNull(raw.url ?? raw.mapsUrl ?? googleBrut.mapsUrl),
      rating: nombreOuNull(raw.totalScore ?? googleBrut.rating),
      reviewCount: nombreOuNull(raw.reviewsCount ?? googleBrut.reviewCount),
      claimed,
      ownerDescription,
      wasOpenAtScrape: booleenOuNull(raw.wasOpenAtScrapeTime),
      searchString: texteOuNull(raw.searchString),
      rank: nombreOuNull(raw.rank),
      imageUrl: texteOuNull(raw.imageUrl),
      permanentlyClosed: false,
      temporarilyClosed: booleenOuNull(raw.temporarilyClosed) === true,
      reviewsDistribution: normaliserDistribution(raw.reviewsDistribution),
      scrapedAt: dateOuNull(raw.scrapedAt),
      additionalInfo:
        raw.additionalInfo && typeof raw.additionalInfo === 'object' ? raw.additionalInfo : {}
    },

    openingHours: parseOpeningHours(raw.openingHours),

    status,
    exclusionReason: status === 'exclu' ? exclusionReason : null,
    score: null,
    scoreDetail: null,

    activitySummary,
    summaryNeedsReview,
    services,
    servicesModifiesPar: null,

    calls: [],
    callNotes: '',
    callAttempts: 0,
    lastCallAt: null,
    nextCallAt: null,
    meetingAt: null,
    lostReason: null,

    source: {
      actor: source.actor || 'compass/crawler-google-places',
      datasetId: source.datasetId || null,
      runDate: source.runDate || formaterDateImport(instant),
      importedFile: source.importedFile || null,
      historique: []
    },
    importedAt: instant,
    importedBy: uid,
    updatedAt: instant,
    updatedBy: uid,

    optedOut: false,
    optedOutAt: null,

    // RÉSERVÉ ÉTAPE 2 — jamais écrit à l'étape 1
    userId: null,
    creationRequestId: null,
    requestCreatedAt: null,

    // RÉSERVÉ ÉTAPE 3 — trigger Stripe uniquement
    proActivatedAt: null,
    proSubscriptionId: null,
    proReactivations: 0,

    // Jalons du tunnel : posés à l'import, remplis au fil des appels
    milestones: {
      importedAt: instant,
      firstCallAt: null,
      qualifiedAt: null,
      requestCreatedAt: null,
      proActivatedAt: null
    }
  };

  const { score, scoreDetail } = scoreProspect(document);

  return { document: { ...document, score, scoreDetail } };
};

/* ─────────────────── 4 bis. Création manuelle (formulaire « ➕ Nouveau prospect ») ─────────────────── */

/**
 * Métiers **suggérés** par le formulaire manuel (aide à la saisie, pas une contrainte).
 *
 * Le champ « Métier » est libre depuis le 30/10/2026 : un prospect peut être maçon, couvreur ou
 * serrurier, et l'étape 1 n'a aucune raison de refuser la fiche. Cette liste n'alimente donc qu'un
 * `<datalist>` : les trois métiers du run de référence, les plus fréquents.
 *
 * `metierDepuisTexteLibre` (catalogue) fait le reste — canonisation tolérante aux accents et aux
 * formes nominales (`Plombier` → `plomberie`).
 */
export const METIERS_MANNUELS = ['plomberie', 'chauffage', 'electricite'];

/**
 * Formulaire « ➕ Nouveau prospect » → document `prospects` **au format exact de l'import**.
 *
 * Le document est produit par `normalizeItem` : le schéma (champs réservés étape 2/3, `milestones`,
 * `source`) ne peut pas diverger de celui du scraper. Trois valeurs sont ensuite imposées, parce
 * qu'elles relèvent de la décision de l'opérateur et non du scrape :
 *  - `targetTrade` — **saisie libre**, canonisée en code interne quand elle est reconnue
 *                    (`Plombier`, `Électricité`, `chauffagiste`…), conservée telle quelle sinon
 *                    (« Froid et climatisation »). Seuls `plomberie` et `chauffage` rapportent les
 *                    10 points de métier (`METIERS_CIBLES`) : c'est le barème, pas la saisie, qui
 *                    restreint ;
 *  - `status`      — `exclu` si le numéro est invalide, sinon `a_appeler` / `a_completer` ;
 *  - `score`       — recalculé, car il dépend de `targetTrade`.
 *
 * Ce qui n'est **pas** inventé : `categories: []`, `google.*` à `null`, `activitySummary: null`,
 * `email: null`, `services: []`. Une fiche manuelle ne prétend rien savoir de l'entreprise.
 *
 * @param {{name?: string, phone?: string, street?: string, postalCode?: string, city?: string,
 *          targetTrade?: string}} formulaire
 * @param {{uid?: string|null, maintenant?: Date}} options
 * @returns {object} document prêt pour `createProspect`
 */
export const construireProspectManuel = (formulaire = {}, { uid = null, maintenant = new Date() } = {}) => {
  const nom = texteOuNull(formulaire.name);
  const phone = normaliserTelephone(formulaire.phone);

  if (!nom) throw new Error('prospectImport: le nom de l’entreprise est obligatoire');
  if (!phone) throw new Error('prospectImport: le téléphone est obligatoire');

  const instant = maintenant instanceof Date ? maintenant : new Date(maintenant);
  const placeId = `manuel-${instant.getTime()}`;

  const { document } = normalizeItem(
    {
      placeId,
      title: nom,
      phoneUnformatted: phone,
      street: texteOuNull(formulaire.street),
      postalCode: texteOuNull(formulaire.postalCode),
      city: texteOuNull(formulaire.city)
    },
    { uid, importedAt: instant, source: { actor: 'manuel', runDate: 'manuel', importedFile: null } }
  );

  // Métier libre : on canonise quand la saisie est reconnaissable, sinon on la garde telle quelle.
  const metierSaisi = texteOuNull(formulaire.targetTrade);
  const targetTrade = metierDepuisTexteLibre(metierSaisi) || metierSaisi;

  // `normalizeItem` a déjà testé `TELEPHONE_REGEX` : on ne refait pas le calcul.
  const status = !document.phoneValid
    ? 'exclu'
    : document.address.isComplete
    ? 'a_appeler'
    : 'a_completer';

  const complet = {
    ...document,
    targetTrade,
    status,
    exclusionReason: status === 'exclu' ? MOTIFS_EXCLUSION.SANS_TELEPHONE : null
  };

  const { score, scoreDetail } = scoreProspect(complet);

  return { ...complet, score, scoreDetail };
};

/**
 * Dédoublonnage du formulaire manuel : **par téléphone normalisé**, jamais par `placeId`
 * (`manuel-<horodatage>` est unique par construction, donc aveugle aux vrais doublons).
 *
 * La comparaison passe par `cleTelephone` : `+33612345678` et `0612345678` sont **le même
 * numéro** — sans cette conversion, un prospect importé en `+33` et saisi à la main en `06`
 * passerait pour deux entreprises distinctes.
 *
 * @returns {object|null} le prospect existant, ou `null`
 */
export const trouverDoublonTelephone = (existants = [], phone) => {
  const cible = cleTelephone(phone);
  if (!cible) return null;
  return (
    (Array.isArray(existants) ? existants : []).find(
      (prospect) => cleTelephone(prospect?.phone) === cible
    ) || null
  );
};

/* ─────────────────── 5. Merge, dédoublonnage et récapitulatif d'import ─────────────────── */

/** `undefined`/`null`/chaîne vide/nombre non fini → « pas de valeur ». */
const estVide = (valeur) =>
  valeur === null ||
  valeur === undefined ||
  (typeof valeur === 'string' && valeur.trim() === '') ||
  (typeof valeur === 'number' && !Number.isFinite(valeur));

/** Donnée Google : la nouvelle valeur n'écrase **jamais** une valeur existante par du vide. */
const prefererNouveau = (nouveau, ancien) => (estVide(nouveau) ? ancien : nouveau);

/**
 * Réimporter un même fichier **ne doit pas écraser les appels déjà passés.**
 *
 * | Catégorie | Comportement |
 * |---|---|
 * | Données Google (`name`, `categories`, `phone`, `address`, `google.*`, `openingHours`, `location`) | écrasées si la nouvelle valeur est non vide |
 * | Données CRM (`status`, `calls`, `callNotes`, `lastCallAt`, `nextCallAt`, `optedOut`, `userId`, `milestones`) | **jamais** écrasées |
 * | Traçabilité (`source`, `importedAt`, `importedBy`) | empilées dans `source.historique[]` |
 *
 * `activitySummary` et `services` ne sont écrasés que si l'opérateur n'y a jamais touché
 * (`servicesModifiesPar === null`).
 */
export const mergeProspect = (existant, entrant) => {
  if (!existant) return entrant;
  if (!entrant) return existant;

  const adresseExistante = existant.address || {};
  const adresseEntrante = entrant.address || {};

  const adresse = {
    street: prefererNouveau(adresseEntrante.street, adresseExistante.street),
    postalCode: prefererNouveau(adresseEntrante.postalCode, adresseExistante.postalCode),
    city: prefererNouveau(adresseEntrante.city, adresseExistante.city),
    formatted: prefererNouveau(adresseEntrante.formatted, adresseExistante.formatted),
    isComplete: false
  };
  adresse.isComplete = Boolean(
    adresse.street &&
      adresse.street.length >= 5 &&
      /^\d{5}$/.test(adresse.postalCode || '') &&
      adresse.city
  );

  const googleExistant = existant.google || {};
  const googleEntrant = entrant.google || {};
  const google = { ...googleExistant };

  Object.keys(googleEntrant).forEach((cle) => {
    if (cle === 'permanentlyClosed') return; // garde-fou d'import, pas une donnée à fusionner
    const valeur = googleEntrant[cle];
    if (estVide(valeur)) return;
    if (cle === 'additionalInfo' || cle === 'reviewsDistribution') {
      google[cle] = { ...(googleExistant[cle] || {}), ...valeur };
      return;
    }
    google[cle] = valeur;
  });

  const sourceExistant = existant.source || {};
  const sourceEntrant = entrant.source || {};
  const historique = Array.isArray(sourceExistant.historique) ? [...sourceExistant.historique] : [];

  historique.push({
    importedAt: existant.importedAt ?? null,
    importedBy: existant.importedBy ?? null,
    runDate: sourceExistant.runDate ?? null,
    importedFile: sourceExistant.importedFile ?? null,
    datasetId: sourceExistant.datasetId ?? null
  });

  /* FUSION */
  const fusion = {
    ...entrant,

    // ── Données Google : non vides seulement ──
    name: prefererNouveau(entrant.name, existant.name),
    categories: existant.categories?.length ? existant.categories : entrant.categories,
    primaryCategory: prefererNouveau(entrant.primaryCategory, existant.primaryCategory),
    targetTrade: prefererNouveau(entrant.targetTrade, existant.targetTrade),
    phone: prefererNouveau(entrant.phone, existant.phone),
    phoneValid: Boolean(prefererNouveau(entrant.phone, existant.phone)) &&
      (existant.phoneValid === true || entrant.phoneValid === true),
    address: adresse,
    departmentCode: prefererNouveau(entrant.departmentCode, existant.departmentCode),
    location: {
      lat: prefererNouveau(entrant.location?.lat, existant.location?.lat),
      lng: prefererNouveau(entrant.location?.lng, existant.location?.lng)
    },
    google,
    openingHours: entrant.openingHours || existant.openingHours,

    // ── Données CRM : jamais écrasées ──
    status: existant.status,
    exclusionReason: existant.exclusionReason,
    calls: Array.isArray(existant.calls) ? existant.calls : [],
    callNotes: existant.callNotes ?? '',
    callAttempts: existant.callAttempts ?? 0,
    lastCallAt: existant.lastCallAt ?? null,
    nextCallAt: existant.nextCallAt ?? null,
    meetingAt: existant.meetingAt ?? null,
    lostReason: existant.lostReason ?? null,
    optedOut: existant.optedOut === true,
    optedOutAt: existant.optedOutAt ?? null,
    email: prefererNouveau(existant.email, entrant.email),
    userId: existant.userId ?? null,
    creationRequestId: existant.creationRequestId ?? null,
    requestCreatedAt: existant.requestCreatedAt ?? null,
    proActivatedAt: existant.proActivatedAt ?? null,
    proSubscriptionId: existant.proSubscriptionId ?? null,
    proReactivations: existant.proReactivations ?? 0,
    milestones: { ...entrant.milestones, ...(existant.milestones || {}) },

    // ── Résumé et services : protégés dès que l'opérateur y a touché ──
    activitySummary:
      existant.servicesModifiesPar === null
        ? prefererNouveau(entrant.activitySummary, existant.activitySummary)
        : existant.activitySummary,
    services:
      existant.servicesModifiesPar === null && entrant.services?.length
        ? entrant.services
        : existant.services || [],
    servicesModifiesPar: existant.servicesModifiesPar ?? null,
    summaryNeedsReview:
      existant.servicesModifiesPar === null
        ? Boolean(entrant.summaryNeedsReview)
        : existant.summaryNeedsReview,

    // ── Traçabilité : le nouveau run n'efface pas l'ancien ──
    source: { ...sourceEntrant, historique },
    importedAt: entrant.importedAt ?? existant.importedAt,
    importedBy: entrant.importedBy ?? existant.importedBy,
    updatedAt: entrant.updatedAt ?? existant.updatedAt,
    updatedBy: entrant.updatedBy ?? existant.updatedBy
  };

  const { score, scoreDetail } = scoreProspect(fusion);

  return { ...fusion, score, scoreDetail };
};

/** Deux entrées du même fichier portant le même `placeId` → **un seul document**. */
export const dedupeByPlaceId = (documents = []) => {
  const parPlaceId = new Map();

  documents.forEach((document) => {
    if (!document || !document.placeId) return;
    if (!parPlaceId.has(document.placeId)) {
      parPlaceId.set(document.placeId, document);
      return;
    }
    parPlaceId.set(document.placeId, mergeProspect(parPlaceId.get(document.placeId), document));
  });

  return [...parPlaceId.values()];
};

/**
 * Récapitulatif **avant écriture** : c'est exactement le code qui tourne au moment de l'import,
 * donc l'aperçu ne peut pas mentir (§5.3).
 *
 * Chaque rejet est compté et listé : l'opérateur doit voir « 50 lues · 46 fermées · 1 sans
 * placeId » plutôt que « 46 importées » tout court.
 *
 * @param {Array} rawItems entrées brutes du fichier
 * @param {{existingById?: object, uid?: string|null, importedAt?: Date, source?: object,
 *          ignoreExisting?: boolean}} options
 * @returns {{documents: Array, rejets: Array, resume: object}}
 */
export const analyserImport = (rawItems, options = {}) => {
  const {
    existingById = {},
    uid = null,
    importedAt = new Date(),
    source = {},
    ignoreExisting = false
  } = options;

  const entrees = Array.isArray(rawItems) ? rawItems : [];
  const rejets = [];
  const parPlaceId = new Map();
  let ignorees = 0;
  let doublons = 0;

  entrees.forEach((raw, index) => {
    const resultat = normalizeItem(raw, { uid, importedAt, source });

    if (resultat.rejet) {
      rejets.push({
        ligne: index + 1,
        motif: resultat.rejet,
        libelle: MOTIFS_REJET_LABELS[resultat.rejet],
        nom: apercuTexte(raw?.title ?? raw?.name ?? raw?.nom ?? '', 60) || null,
        placeId: texteOuNull(raw?.placeId ?? raw?.place_id ?? raw?.id)
      });
      return;
    }

    const { document } = resultat;
    const existant = existingById[document.placeId];

    // « ignorer les fiches déjà en base » : import strictement additif, aucune réécriture.
    if (existant && ignoreExisting) {
      ignorees += 1;
      return;
    }

    if (parPlaceId.has(document.placeId)) doublons += 1;

    const base = parPlaceId.get(document.placeId) || existant || null;
    parPlaceId.set(document.placeId, base ? mergeProspect(base, document) : document);
  });

  const documents = [...parPlaceId.values()];
  const compter = (predicat) => documents.filter(predicat).length;

  return {
    documents,
    rejets,
    resume: {
      lues: entrees.length,
      ecrits: documents.length,
      // Fiches qui entrent réellement dans la file d'appel (hors `exclu` et hors opposition)
      appelables: compter((doc) => doc.status !== 'exclu' && doc.optedOut !== true),
      rejets: rejets.length,
      doublons,
      ignorees,
      fermees: rejets.filter((rejet) => rejet.motif === MOTIFS_REJET.FERME_DEFINITIVEMENT).length,
      sansPlaceId: rejets.filter((rejet) => rejet.motif === MOTIFS_REJET.SANS_PLACEID).length,
      sansNom: rejets.filter((rejet) => rejet.motif === MOTIFS_REJET.SANS_NOM).length,
      invalides: rejets.filter((rejet) => rejet.motif === MOTIFS_REJET.INVALIDE).length,
      sansTelephone: compter((doc) => doc.exclusionReason === MOTIFS_EXCLUSION.SANS_TELEPHONE),
      horsCible: compter((doc) => doc.exclusionReason === MOTIFS_EXCLUSION.HORS_CIBLE),
      aCompleter: compter((doc) => doc.status === 'a_completer'),
      aAppeler: compter((doc) => doc.status === 'a_appeler'),
      avecServices: compter((doc) => Array.isArray(doc.services) && doc.services.length > 0),
      avecResume: compter((doc) => typeof doc.activitySummary === 'string')
    }
  };
};

/** Phrase de récapitulatif affichée dans la modale d'import. */
export const formaterResumeImport = (resume = {}) => {
  const morceaux = [
    `${resume.lues || 0} fiche${(resume.lues || 0) > 1 ? 's' : ''} lue${(resume.lues || 0) > 1 ? 's' : ''}`,
    `${resume.ecrits || 0} importable${(resume.ecrits || 0) > 1 ? 's' : ''}`,
    `${resume.sansTelephone || 0} sans téléphone`,
    `${resume.fermees || 0} fermée${(resume.fermees || 0) > 1 ? 's' : ''}`,
    `${resume.aCompleter || 0} à compléter`
  ];

  if (resume.ignorees) {
    morceaux.push(`${resume.ignorees} déjà en base (ignorée${resume.ignorees > 1 ? 's' : ''})`);
  }
  if (resume.doublons) {
    morceaux.push(`${resume.doublons} doublon${resume.doublons > 1 ? 's' : ''} fusionné${resume.doublons > 1 ? 's' : ''}`);
  }

  return morceaux.join(' · ');
};
